import React, { useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { getHasClientAccountAccess } from "api/user";
import { getUserId, getCustomerDetails, getUserPermissionsList } from "selectors";
import { useGlobalPageLoading } from "components/GlobalPageLoadingProvider";
import { useGlobalToast } from "components/GlobalToastProvider";
import actions from "../../actions";
import SidebarContainer from "./Sidebar";
import { ClientDetailsRouter } from "./ClientDetailsRouter";

/**
 * Entry point for the Client Profile section (`/clients/:id/:section`).
 *
 * Handles access-control gating and loads core customer details before
 * rendering the sidebar + section router.
 * Section-specific data fetching is delegated to individual sections.
 */
export function ClientDetails() {
  const { id: clientId } = useParams<{ id: string; section: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const clinicianUserId = useSelector(getUserId);
  const customerDetails = useSelector(getCustomerDetails);
  const userPermissions = useSelector(getUserPermissionsList);
  const { showLoading } = useGlobalPageLoading() as { showLoading: (loading?: boolean) => void };
  const { showToast } = useGlobalToast();

  // Set page details for LayoutProvider permission check
  useEffect(() => {
    if (userPermissions?.view_client_list_by_workflow_state) {
      dispatch(
        actions.setPageDetails({
          currentPage: "customerdetail",
          pageName: "Client Detail",
          menu: "entities",
          hideDivider: true,
        })
      );
    } else if (userPermissions?.view_my_clients) {
      dispatch(
        actions.setPageDetails({
          currentPage: "customerview",
          pageName: "Client View",
          menu: "entities",
          hideDivider: true,
        })
      );
    }
  }, [userPermissions, dispatch]);

  useEffect(() => {
    if (clientId) {
      dispatch(actions.loadCustomerDetails(clientId));
    }
  }, [clientId, dispatch]);

  // Set client name in page header
  useEffect(() => {
    const name = customerDetails?.details?.name;
    const lastName = customerDetails?.details?.child_last_name;
    if (name) {
      const altTitle = [name, lastName].filter(Boolean).join(" ");
      dispatch(actions.setPageDetails({ altTitle }));
    }
    return () => {
      dispatch(actions.setPageDetails({ altTitle: null }));
    };
  }, [customerDetails?.details?.name, customerDetails?.details?.child_last_name, dispatch]);

  useQuery(
    ["getHasClientAccountAccess", clinicianUserId, clientId],
    () => getHasClientAccountAccess({ userId: clinicianUserId, clientId }),
    {
      enabled: !!clinicianUserId && !!clientId,
      onSuccess: ({ data }: { data: { hasAccess: boolean } }) => {
        if (data.hasAccess) {
          showLoading(false);
        } else {
          history.push("/dashboard");
          showToast({ errorState: true, message: "You do not have access to this client" });
        }
      },
      onError: () => {
        history.push("/dashboard");
        showToast({ errorState: true, message: "Error accessing this client" });
      },
      refetchOnWindowFocus: false,
    }
  );

  return (
    <SidebarContainer>
      <ClientDetailsRouter key={clientId} />
    </SidebarContainer>
  );
}

export default ClientDetails;
